import React, { FunctionComponent, useState } from 'react';
import {
  OverlayTrigger,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap';
import { useGrowthBook } from 'hooks/GrowthBook';
import { getPdfPreviewParams } from 'util/tsutils';

import BackendService from '../../services/BackendService';
import BlurInput from '../BlurInput';
import {
  AnnotationLike,
  Change,
  AnnotationAddType,
  Signer,
  Signature,
  AnnotationAddMetaType,
} from './types';
import { FIELD_NAME_PREFILL_OPTIONS } from './fieldNamePrefill';

export const normalizeFieldType = (fieldType: string | undefined) => {
  switch (fieldType) {
    case 'Tx':
    case AnnotationAddType.TEXTBOX:
      return AnnotationAddType.TEXTBOX;
    case 'Btn':
    case AnnotationAddType.CHECKBOX:
      return AnnotationAddType.CHECKBOX;
    case 'Sig':
    case AnnotationAddType.SIGNATURE:
      return AnnotationAddType.SIGNATURE;
    default:
      return null;
  }
};

const getFieldTypeFriendlyName = (fieldType: string | undefined) => {
  switch (normalizeFieldType(fieldType)) {
    case AnnotationAddType.TEXTBOX:
      return 'Text Box';
    case AnnotationAddType.CHECKBOX:
      return 'Check Box';
    case AnnotationAddType.SIGNATURE:
      return 'Signature';
    default:
      return 'Unknown';
  }
};

interface Props {
  formId: string;
  resetView: () => void;
  onSave: () => void;

  changes: Change[];
  recordChange: (change: Change) => void;
  undoChange: () => void;

  selectedAnnotation: AnnotationLike | undefined;
  clearSelectedAnnotation: () => void;

  addingAnnotation: AnnotationAddMetaType | undefined;
  onAddAnnotation: (annotation: AnnotationAddMetaType | undefined) => void;

  useSmartAddAnnotation: boolean;
  setUseSmartAddAnnotation: (value: boolean) => void;
}

export const FormPDFEditorSideBar: FunctionComponent<Props> = ({
  formId,
  resetView,
  onSave,
  changes,
  recordChange,
  undoChange,
  selectedAnnotation,
  clearSelectedAnnotation,
  addingAnnotation,
  onAddAnnotation,
  useSmartAddAnnotation,
  setUseSmartAddAnnotation,
}) => {
  const [prefillSearch, setPrefillSearch] = useState('');
  const [signer, setSigner] = useState(Signer.APPLICANT);
  const [signature, setSignature] = useState(Signature.SIGNATURE);

  const renderAnnotationEditor = () => {
    if (!selectedAnnotation) return null;
    const currentChange = changes.find(
      ({ id }) => id === selectedAnnotation.id,
    );

    return (
      <>
        <div className="selector" />
        <div className="annotation-field-type">
          Field Type: {getFieldTypeFriendlyName(selectedAnnotation.fieldType)}
        </div>
        <button
          onClick={() => {
            recordChange({
              type: 'change',
              originalFieldName: selectedAnnotation.fieldName || '',
              id: selectedAnnotation.id || '',
              deleted: true,
            });
            clearSelectedAnnotation();
          }}
        >
          Delete Field
        </button>
        <button onClick={clearSelectedAnnotation}>Clear Selection</button>
        <fieldset>
          <legend>Field Name</legend>
          <BlurInput
            value={currentChange?.fieldName ?? selectedAnnotation.fieldName}
            onChangeBlur={value => {
              recordChange({
                type: 'change',
                originalFieldName: selectedAnnotation.fieldName || '',
                id: selectedAnnotation.id || '',
                fieldName: value,
              });
            }}
          />
        </fieldset>
        <div className="annotation-name-prefill">
          <div className="prefill-title">Quick Field Name</div>
          <input
            placeholder="Search"
            value={prefillSearch}
            onChange={event => setPrefillSearch(event.target.value)}
          />
          <div className="prefill-options-container">
            <div className="prefill-options">
              {FIELD_NAME_PREFILL_OPTIONS.filter(option => {
                if (prefillSearch.trim()) {
                  return option
                    .toLowerCase()
                    .includes(prefillSearch.replace(/\s+/g, '').toLowerCase());
                }
                return true;
              }).map(option => (
                <button
                  key={option}
                  onClick={() => {
                    recordChange({
                      type: 'change',
                      originalFieldName: selectedAnnotation.fieldName || '',
                      id: selectedAnnotation.id || '',
                      fieldName: option,
                    });
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  };

  const growthbook = useGrowthBook();
  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const response = await BackendService.get(
      `/formTemplates/${formId}/preview`,
      { params: getPdfPreviewParams(growthbook) },
    );
    const PDFLink = window.URL.createObjectURL(response);
    window.open(PDFLink);
    window.URL.revokeObjectURL(PDFLink);
  };

  return (
    <div className="form-editor-sidebar">
      {changes.length > 0 ? (
        <OverlayTrigger
          placement="top"
          delayHide={300}
          overlay={
            <Tooltip id="form-editor-sidebar-preview-tooltip">
              Save to Preview PDF
            </Tooltip>
          }
        >
          <button className="disabled">Preview Form</button>
        </OverlayTrigger>
      ) : (
        <button onClick={handleClick}>Preview Form</button>
      )}
      <button onClick={resetView}>Reset View</button>
      <div className="selector" />
      <label>
        <input
          type="checkbox"
          checked={useSmartAddAnnotation}
          onChange={e => setUseSmartAddAnnotation(e.target.checked)}
        />
        Use Smart Field Sizing
      </label>
      <button
        className="add-textbox"
        onClick={() =>
          onAddAnnotation({
            type: AnnotationAddType.TEXTBOX,
          })
        }
      >
        Add Text Box
      </button>
      <button
        className="add-checkbox"
        onClick={() =>
          onAddAnnotation({
            type: AnnotationAddType.CHECKBOX,
          })
        }
      >
        Add Check Box
      </button>
      <button
        className="add-signature"
        onClick={() =>
          onAddAnnotation({
            type: AnnotationAddType.SIGNATURE,
            signer,
            signature,
          })
        }
      >
        Add Signature Field
      </button>
      {addingAnnotation &&
        addingAnnotation.type === AnnotationAddType.SIGNATURE && (
          <>
            <div className="btn-group-label">Signer</div>
            <ToggleButtonGroup
              type="radio"
              name="signer"
              value={signer}
              onChange={newSigner => {
                setSigner(newSigner);
                onAddAnnotation({
                  type: AnnotationAddType.SIGNATURE,
                  signer: newSigner,
                  signature,
                });
              }}
            >
              <ToggleButton value={Signer.APPLICANT}>Applicant</ToggleButton>
              <ToggleButton value={Signer.COAPPLICANT}>
                Co-Applicant
              </ToggleButton>
              <ToggleButton value={Signer.MANAGER}>Manager</ToggleButton>
            </ToggleButtonGroup>
            <div className="btn-group-label">Signature</div>
            <ToggleButtonGroup
              type="radio"
              name="signature"
              value={signature}
              onChange={newSignature => {
                setSignature(newSignature);
                onAddAnnotation({
                  type: AnnotationAddType.SIGNATURE,
                  signer,
                  signature: newSignature,
                });
              }}
            >
              <ToggleButton value={Signature.SIGNATURE}>Signature</ToggleButton>
              <ToggleButton value={Signature.INITIALS}>Initials</ToggleButton>
            </ToggleButtonGroup>
          </>
        )}
      {addingAnnotation && (
        <button
          onClick={() => onAddAnnotation(undefined)}
          className="cancel-add"
        >
          Cancel
        </button>
      )}
      {changes.length > 0 && (
        <>
          <div className="selector" />
          <button onClick={undoChange}>Undo</button>
          <button className="save-pdf" onClick={onSave}>
            Save PDF
          </button>
        </>
      )}
      {renderAnnotationEditor()}
    </div>
  );
};
